import React, { useContext, useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Button from "../../shared/components/FormElements/Button";
import LoadingSpinner from "../../shared/components/UIElements/LoadingSpinner";
import Modal from "../../shared/components/UIElements/Modal";
import { AuthContext } from "../../shared/context/auth-context";
import InstrumentCharts from "../components/InstrumentCharts";
import InstrumentChartsPerformance from "../components/InstrumentChartsPerformance";
import Input from "../../shared/components/FormElements/Input";
import { useForm } from "../../shared/hooks/form-hook";
import Card from "../../shared/components/UIElements/Card";
import {
  VALIDATOR_MINLENGTH,
  VALIDATOR_REQUIRE,
} from "../../shared/util/validators";
import { canManagePortfolio } from "../../shared/util/roles";
import TradeList from "../components/TradeList";
import TradeSummary from "../components/TradeSummary";
import ReactSlider from "react-slider";
import "./InstrumentDetails.css";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const authHeaders = (token) => ({
  "Content-Type": "application/json",
  Authorization: "Bearer " + token,
});

const fetchJson = async (url, token, signal) => {
  const res = await fetch(url, {
    method: "GET",
    headers: authHeaders(token),
    signal,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || `HTTP ${res.status}`);
  }
  return res.json();
};

// ─── Component ────────────────────────────────────────────────────────────────
const InstrumentDetails = () => {
  const { iid } = useParams();
  const navigate = useNavigate();
  const auth = useContext(AuthContext);

  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const [loadedDataInstrument, setLoadedDataInstrument] = useState(null);
  const [loadedTradeSummary, setLoadedTradeSummary] = useState(null);
  const [loadedTrades, setLoadedTrades] = useState(null);
  const [loadedDataAnalytics, setLoadedDataAnalytics] = useState(null);

  const [selectedTrade, setSelectedTrade] = useState(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  // Who holds the instrument in users' accounts: { loading } | { data } | { error }
  const [holders, setHolders] = useState(null);

  // Chart range state
  const [rangeValues, setRangeValues] = useState([0, 0]);

  // Track threshold updates to trigger re-fetch
  const [updateCount, setUpdateCount] = useState(0);

  // ─── Memoized handlers ────────────────────────────────────────────────────
  const handleTradeClick = useCallback(
    (trade) => {
      // The selected trade clicked again: unselect it, back to the whole range
      if (trade && selectedTrade && trade.TRADE_NUMBER === selectedTrade.TRADE_NUMBER) {
        setSelectedTrade(null);
        if (loadedDataAnalytics) {
          setRangeValues([0, loadedDataAnalytics.dates.length - 1]);
        }
        return;
      }

      setSelectedTrade(trade);

      if (trade && loadedDataAnalytics) {
        const { DATE_BUY, DATE_SELL } = trade;
        const dates = loadedDataAnalytics.dates;

        const startIndex = dates.indexOf(DATE_BUY);
        let endIndex = DATE_SELL ? dates.indexOf(DATE_SELL) : dates.length - 1;

        if (startIndex !== -1 && endIndex !== -1) {
          const tradeDuration = endIndex - startIndex;
          const padding = Math.max(10, Math.floor(tradeDuration * 0.1));
          setRangeValues([
            Math.max(0, startIndex - padding),
            Math.min(dates.length - 1, endIndex + padding),
          ]);
        }
      }
    },
    [loadedDataAnalytics, selectedTrade]
  );

  // A range chosen below the charts (slider or button) unselects the trade
  const handleSliderChange = useCallback((values) => {
    setSelectedTrade(null);
    setRangeValues(values);
  }, []);

  const resetSlider = useCallback(() => {
    if (loadedDataAnalytics) {
      setSelectedTrade(null);
      setRangeValues([0, loadedDataAnalytics.dates.length - 1]);
    }
  }, [loadedDataAnalytics]);

  const handleRangeClick = useCallback(
    (rangeInDays) => {
      if (!loadedDataAnalytics) return;

      const dates = loadedDataAnalytics.dates;
      const endIndex = dates.length - 1;
      const endDate = new Date(dates[endIndex]);
      const startDate = new Date(endDate);
      startDate.setDate(endDate.getDate() - rangeInDays);

      let startIndex = 0;
      for (let i = 0; i < dates.length; i++) {
        if (new Date(dates[i]) >= startDate) {
          startIndex = i;
          break;
        }
      }

      setSelectedTrade(null);
      setRangeValues([startIndex, endIndex]);
    },
    [loadedDataAnalytics]
  );

  const showParameterUpdateHandler = useCallback(() => setShowUpdateModal(true), []);
  // The removal is refused while its trade is open or MTT holds the
  // instrument in an account it trades (backend/util/instrument-trade.js,
  // instrument-holders.js); the dialog says so up front
  const showDeleteWarningHandler = useCallback(() => {
    setShowConfirmModal(true);
    setHolders({ loading: true });
    fetchJson(`/api/instruments/${iid}/holders`, auth.token)
      .then((data) => setHolders({ data }))
      .catch((err) => setHolders({ error: err.message }));
  }, [auth.token, iid]);

  const cancelDeleteHandler = useCallback((event) => {
    event.preventDefault();
    setShowConfirmModal(false);
    setShowUpdateModal(false);
  }, []);

  const [formState, inputHandler] = useForm({
    rsibuy: {
      value: loadedDataInstrument?.instrument.triggerThresholds.RSI_BUY || 30,
      isValid: true,
    },
    rsisell: {
      value: loadedDataInstrument?.instrument.triggerThresholds.RSI_SELL || 70,
      isValid: true,
    },
    stoploss: {
      value: loadedDataInstrument?.instrument.triggerThresholds.STOP_LOSS || 100,
      isValid: true,
    },
  });

  // ─── Data fetching ─────────────────────────────────────────────────────────
  // Each part is shown as soon as it arrives: the instrument, its trade summary
  // and its trades come quickly, the daily series for the charts take longest.
  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    const fail = (err) => {
      if (err.name !== "AbortError") {
        setError(err.message || "An error occurred while fetching data");
      }
    };

    setIsLoading(true);
    setError(null);
    setLoadedDataInstrument(null);
    setLoadedTradeSummary(null);
    setLoadedTrades(null);
    setLoadedDataAnalytics(null);

    fetchJson(`/api/instruments/trade_summary/${iid}`, auth.token, signal)
      .then(setLoadedTradeSummary)
      .catch(fail);

    // The instrument gives the ticker the trades and the series are read by
    fetchJson(`/api/instruments/${iid}`, auth.token, signal)
      .then((instrumentData) => {
        setLoadedDataInstrument(instrumentData);
        setIsLoading(false);

        const ticker = instrumentData?.instrument?.ticker;
        if (!ticker) throw new Error("Instrument has no ticker");

        fetchJson(`/api/analytics/get_trades/${ticker}`, auth.token, signal)
          .then(setLoadedTrades)
          .catch(fail);
        return fetchJson(`/api/analytics/${ticker}`, auth.token, signal).then((analyticsData) => {
          // Set together, so the charts are drawn once, with the whole range
          setRangeValues([0, analyticsData.dates.length - 1]);
          setLoadedDataAnalytics(analyticsData);
        });
      })
      .catch((err) => {
        fail(err);
        if (!signal.aborted) {
          setIsLoading(false);
        }
      });

    return () => controller.abort();
  }, [auth.token, iid, updateCount]);

  // Reset selected trade when loadedTrades changes
  useEffect(() => {
    setSelectedTrade(null);
  }, [loadedTrades]);

  // ─── Threshold Update ──────────────────────────────────────────────────────
  const confirmUpdateHandler = useCallback(async () => {
    setIsLoading(true);
    setShowUpdateModal(false);

    try {
      const response = await fetch(`/api/instruments/update_thresholds/${iid}`, {
        method: "PATCH",
        headers: authHeaders(auth.token),
        body: JSON.stringify({
          RSI_BUY: formState.inputs.rsibuy.value,
          RSI_SELL: formState.inputs.rsisell.value,
          MACD_BUY: 1,
          MACD_SELL: -1,
          STOP_LOSS: formState.inputs.stoploss.value,
        }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || "Failed to update");
      }

      setUpdateCount((c) => c + 1); // triggers re-fetch
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [auth.token, iid, formState]);

  // ─── Delete Instrument ─────────────────────────────────────────────────────
  const confirmDeleteHandler = useCallback(async () => {
    setShowConfirmModal(false);
    setIsLoading(true);

    try {
      // The backend also drops the instrument's price and trades collections
      const res = await fetch(`/api/instruments/${iid}`, {
        method: "DELETE",
        headers: authHeaders(auth.token),
      });

      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.message || "Failed to delete instrument");

      const ticker = body.ticker || "The instrument";
      navigate("/portfolio", {
        state: {
          notice: body.fundRefresh === "failed"
            ? `${ticker} was removed. The fund couldn't be recalculated now; the nightly run at 02:00 does it.`
            : `${ticker} was removed. The fund, the portfolio summary and the report are being recalculated without it, which takes about a minute.`,
        },
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [auth.token, iid, navigate]);

  // ─── Render ─────────────────────────────────────────────────────────────────
  const tickerName = loadedDataInstrument?.instrument?.ticker || "this instrument";
  return (
    <React.Fragment>
      <Modal
        show={showUpdateModal}
        onCancel={cancelDeleteHandler}
        header="UPDATE THRESHOLDS"
        footerClass="place-item__modal-actions"
        footer={
          <React.Fragment>
            <div className="button-group">
              <Button type="button" inverse onClick={cancelDeleteHandler}>
                CANCEL
              </Button>
              <Button type="button" danger onClick={confirmUpdateHandler}>
                UPDATE
              </Button>
            </div>
          </React.Fragment>
        }
      >
        <div id="updateForm">
          <Input
            id="rsibuy"
            element="input"
            type="number"
            label="RSI BUY"
            initialValue={
              loadedDataInstrument?.instrument.triggerThresholds.RSI_BUY || 30
            }
            validators={[VALIDATOR_REQUIRE(), VALIDATOR_MINLENGTH(1)]}
            errorText="Provide valid input."
            onInput={inputHandler}
          />
          <Input
            id="rsisell"
            element="input"
            type="number"
            label="RSI SELL"
            initialValue={
              loadedDataInstrument?.instrument.triggerThresholds.RSI_SELL || 70
            }
            validators={[VALIDATOR_REQUIRE(), VALIDATOR_MINLENGTH(1)]}
            errorText="Provide valid input."
            onInput={inputHandler}
          />
          <Input
            id="stoploss"
            element="input"
            type="number"
            label="Stop Loss"
            initialValue={
              loadedDataInstrument?.instrument.triggerThresholds.STOP_LOSS || 15
            }
            validators={[VALIDATOR_REQUIRE(), VALIDATOR_MINLENGTH(1)]}
            errorText="Provide valid input."
            onInput={inputHandler}
          />
        </div>
      </Modal>
      <Modal
        show={showConfirmModal}
        onCancel={cancelDeleteHandler}
        header="Are you sure?"
        footerClass="place-item__modal-actions"
        footer={
          <React.Fragment>
            <div className="button-group">
              <Button inverse onClick={cancelDeleteHandler}>
                CANCEL
              </Button>
              <Button danger onClick={confirmDeleteHandler} disabled={!holders?.data || holders.data.blocked}>
                DELETE
              </Button>
            </div>
          </React.Fragment>
        }
      >
        {holders?.loading && <p>Checking whether the trade of {tickerName} is open or accounts hold it…</p>}
        {holders?.error && <p>{holders.error}</p>}
        {holders?.data?.blocked && <p>{holders.data.message}</p>}
        {holders?.data && !holders.data.blocked && (
          <>
            <p>
              Delete {tickerName}? Its prices and trades are removed, and the fund, the portfolio summary and the
              report are recalculated without it (about a minute). This can't be undone.
            </p>
            {holders.data.unmanaged.total > 0 && <p>{holders.data.message}</p>}
          </>
        )}
      </Modal>
      {isLoading && (
        <div className="center">
          <LoadingSpinner asOverlay message="LOADING " />
        </div>
      )}
      {error && (
        <div className="center">
          <Card>
            <h2>Error: {error}</h2>
            <Button onClick={() => setError(null)}>Clear Error</Button>
          </Card>
        </div>
      )}
      {loadedDataInstrument && (
        <div className="trade-list">
          <h1>
            {loadedDataInstrument.instrument.ticker}:{" "}
            {loadedDataInstrument.instrument.title} (Stop Loss @{" "}
            {loadedDataInstrument.instrument.triggerThresholds.STOP_LOSS}%)
          </h1>
          <TradeSummary
            data={loadedTradeSummary}
            stop_loss={
              loadedDataInstrument.instrument.triggerThresholds.STOP_LOSS
            }
          />
        </div>
      )}
      {loadedTrades && (
        <div className="trade-list">
          <TradeList
            data={loadedTrades}
            onTradeClick={handleTradeClick}
            selectedTrade={selectedTrade}
          />
        </div>
      )}
      {loadedDataInstrument && !loadedDataAnalytics && !error && (
        <div className="center">
          <LoadingSpinner message="LOADING CHARTS " />
        </div>
      )}
      {loadedDataAnalytics && (
        <InstrumentChartsPerformance data={loadedDataAnalytics} rangeValues={rangeValues} />
      )}
      {loadedDataInstrument && loadedDataAnalytics && (
        <InstrumentCharts
          instrument={loadedDataInstrument.instrument}
          data={loadedDataAnalytics}
          rangeValues={rangeValues}
        />
      )}

      {/* The visible range: stays at the bottom of the window while scrolling
          (InstrumentDetails.css) */}
      {loadedDataAnalytics && (
        <div className="chart-controls">
          <ReactSlider
            className="horizontal-slider"
            thumbClassName="thumb"
            trackClassName="track"
            defaultValue={[0, loadedDataAnalytics.dates.length - 1]}
            ariaLabel={["Lower thumb", "Upper thumb"]}
            ariaValuetext={(state) => `Thumb value ${state.valueNow}`}
            min={0}
            max={loadedDataAnalytics.dates.length - 1}
            value={rangeValues}
            onChange={handleSliderChange}
          />
          <div className="button-group">
            <Button onClick={() => handleRangeClick(7)}>View Past Week</Button>
            <Button onClick={() => handleRangeClick(30)}>
              View Past Month
            </Button>
            <Button onClick={() => handleRangeClick(180)}>
              View Past 6 Months
            </Button>
            <Button onClick={() => handleRangeClick(365)}>
              View Past Year
            </Button>
            <Button onClick={() => handleRangeClick(2 * 365)}>
              View Past 2 Years
            </Button>
            <Button onClick={resetSlider}>View Since Inception</Button>
          </div>
        </div>
      )}
      {loadedDataAnalytics && (
        <div className="button-group instrument-actions">
          {/* Changing instruments is for admins and portfolio managers on the backend */}
          {canManagePortfolio(auth.role) && (
            <>
              <Button
                onClick={showParameterUpdateHandler}
                disabled={!loadedDataInstrument}
              >
                UPDATE THRESHOLDS
              </Button>
              <Button onClick={showDeleteWarningHandler}>
                REMOVE INSTRUMENT
              </Button>
            </>
          )}
          <Button to="/">BACK to Dashboard</Button>
        </div>
      )}
    </React.Fragment>
  );
};

export default InstrumentDetails;
